/**
 * Weekly Selections hook
 * TanStack Query hooks for weekly meal selection management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWeeklySelections,
  bulkUpdateWeeklySelections,
  getAvailableMeals,
  getFamilySummary,
} from '@/lib/api/weekly-selections';
import type {
  GetWeeklySelectionsQuery,
  BulkWeeklySelectionsRequest,
  GetAvailableMealsQuery,
} from '@/types/api';
import type {
  GetFamilySummaryQuery,
  FamilySummaryResponse,
} from '@/types/weekly-selections';
import { weeklySelectionKeys as baseWeeklySelectionKeys } from '@/lib/query-keys';

/**
 * Query keys for weekly-selections-related queries
 * Extended from base keys with additional frontend-specific keys
 */
export const weeklySelectionKeys = {
  ...baseWeeklySelectionKeys,
  familySummary: (params?: GetFamilySummaryQuery) =>
    [...baseWeeklySelectionKeys.all, 'family-summary', params] as const,
};

/**
 * Hook to fetch user's weekly meal selections
 *
 * Features:
 * - Get selections for specific week
 * - Returns meals with user's selection count
 * - Used to display what user has already selected
 *
 * @example
 * ```tsx
 * function WeeklySelectionPage({ userId }: { userId: number }) {
 *   const weekStart = formatDateForAPI(getWeekStart(new Date()));
 *   const { data, isLoading } = useWeeklySelections({
 *     userId,
 *     weekStartDate: weekStart,
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       You've selected {data.selections.length} meals
 *     </div>
 *   );
 * }
 * ```
 */
export function useWeeklySelections(params: GetWeeklySelectionsQuery) {
  return useQuery({
    queryKey: weeklySelectionKeys.selections(params),
    queryFn: () => getWeeklySelections(params),
    enabled: !!(params?.userId && params?.weekStartDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch available meals for selection
 *
 * Features:
 * - Get all meals available for selection
 * - Includes recommendation scores if user has preferences
 * - Shows user's past preferences for each meal
 * - Used to display meal grid/list for selection
 *
 * @example
 * ```tsx
 * function MealSelectionGrid({ userId }: { userId: number }) {
 *   const weekStart = formatDateForAPI(getWeekStart(new Date()));
 *   const { data, isLoading } = useAvailableMeals({
 *     userId,
 *     weekStartDate: weekStart,
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div className="grid grid-cols-3 gap-4">
 *       {data.meals.map(meal => (
 *         <MealCard key={meal.id} meal={meal} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailableMeals(params: GetAvailableMealsQuery) {
  return useQuery({
    queryKey: weeklySelectionKeys.availableMeals(params),
    queryFn: () => getAvailableMeals(params),
    enabled: !!(params?.userId && params?.weekStartDate),
    staleTime: 1000 * 60 * 10, // 10 minutes (meals don't change often)
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch family meal selections summary
 *
 * Features:
 * - Get all family members' selections for a week
 * - Shows each member with their selected meals
 * - Used by organizers to view family-wide selections
 *
 * @example
 * ```tsx
 * function FamilySelectionView() {
 *   const weekStart = formatDateForAPI(getWeekStart(new Date()));
 *   const { data, isLoading } = useFamilySummary({
 *     weekStartDate: weekStart,
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data.members.map(member => (
 *         <FamilyMemberCard key={member.userId} member={member} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFamilySummary(
  params: GetFamilySummaryQuery | undefined
) {
  return useQuery<FamilySummaryResponse>({
    queryKey: params ? weeklySelectionKeys.familySummary(params) : ['disabled'],
    queryFn: () => {
      if (!params) {
        return Promise.reject(new Error('No params provided'));
      }
      return getFamilySummary(params);
    },
    enabled: !!params,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30,   // 30 minutes
  });
}

/**
 * Hook to bulk update weekly meal selections
 *
 * Features:
 * - Replace all selections for a week at once
 * - Optimistic updates for better UX
 * - Automatic cache invalidation
 * - Creates votes for selected meals automatically
 *
 * @example
 * ```tsx
 * function WeeklySelectionForm({ userId }: { userId: number }) {
 *   const [selectedMeals, setSelectedMeals] = useState<number[]>([]);
 *   const weekStart = formatDateForAPI(getWeekStart(new Date()));
 *   const bulkUpdateMutation = useBulkUpdateWeeklySelections();
 *
 *   const handleSave = () => {
 *     bulkUpdateMutation.mutate({
 *       userId,
 *       weekStartDate: weekStart,
 *       selections: selectedMeals.map(mealId => ({ mealId })),
 *     });
 *   };
 *
 *   return (
 *     <button
 *       onClick={handleSave}
 *       disabled={bulkUpdateMutation.isPending}
 *     >
 *       {bulkUpdateMutation.isPending ? 'Saving...' : 'Save Selections'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useBulkUpdateWeeklySelections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkWeeklySelectionsRequest) => bulkUpdateWeeklySelections(data),
    onSuccess: (_response, variables) => {
      // Invalidate weekly selections for this user and week
      queryClient.invalidateQueries({
        queryKey: weeklySelectionKeys.selections({
          userId: variables.userId,
          weekStartDate: variables.weekStartDate,
        }),
      });

      // Invalidate available meals for this user and week
      queryClient.invalidateQueries({
        queryKey: weeklySelectionKeys.availableMeals({
          userId: variables.userId,
          weekStartDate: variables.weekStartDate,
        }),
      });

      // Invalidate family summary (for organizers)
      queryClient.invalidateQueries({
        queryKey: weeklySelectionKeys.familySummary({
          weekStartDate: variables.weekStartDate,
        }),
      });

      // Invalidate user preferences since votes were created
      queryClient.invalidateQueries({
        queryKey: ['preferences', 'user', variables.userId],
      });
    },
  });
}
